import React, { createContext, useContext, useState, useEffect } from "react";

interface FixedCost {
  category: string;
  amount: number;
  description: string;
}

interface FixedCostsContextType {
  fixedCosts: FixedCost[];
  totalFixedCosts: number;
  addFixedCost: (category: string, amount: number, description: string) => void;
  removeFixedCost: (index: number) => void;
  resetFixedCosts: () => void;
  updateFixedCost: (
    index: number,
    category: string,
    amount: number,
    description: string
  ) => void;
}

const FixedCostsContext = createContext<FixedCostsContextType | undefined>(
  undefined
);

export const useFixedCosts = () => {
  const context = useContext(FixedCostsContext);
  if (!context) {
    throw new Error("useFixedCosts must be used within a FixedCostsProvider");
  }
  return context;
};

export const FixedCostsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [fixedCosts, setFixedCosts] = useState<FixedCost[]>(() => {
    const stored = localStorage.getItem("fixedCosts");

    return stored ? JSON.parse(stored) : [];
  });

  // Speichere Fixkosten im localStorage
  useEffect(() => {
    localStorage.setItem("fixedCosts", JSON.stringify(fixedCosts));
  }, [fixedCosts]);

  const addFixedCost = (
    category: string,
    amount: number,
    description: string
  ) => {
    setFixedCosts((prev) => [...prev, { category, amount, description }]);
  };

  const removeFixedCost = (index: number) => {
    setFixedCosts((prev) => prev.filter((_, i) => i !== index));
  };

  const resetFixedCosts = () => {
    setFixedCosts([]);
  };

  const updateFixedCost = (
    index: number,
    category: string,
    amount: number,
    description: string
  ) => {
    setFixedCosts((prev) => {
      const updated = [...prev];
      updated[index] = { category, amount, description };
      return updated;
    });
  };
  const totalFixedCosts = fixedCosts.reduce(
    (sum, item) => sum + item.amount,
    0
  );

  return (
    <FixedCostsContext.Provider
      value={{
        fixedCosts,
        addFixedCost,
        removeFixedCost,
        resetFixedCosts,
        updateFixedCost,
        totalFixedCosts,
      }}
    >
      {children}
    </FixedCostsContext.Provider>
  );
};
